import React, { useState, useEffect } from 'react';
import {
  IonContent,
  IonPage,
  IonGrid,
  IonRow,
  IonCol,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonIcon,
  IonItem,
  IonLabel,
  IonSkeletonText,
  IonButton,
} from '@ionic/react';
import {
  eyeOutline,
  linkOutline,
  trendingUpOutline,
  timeOutline,
  cloudDownloadOutline,
  peopleOutline,
  informationCircleOutline,
} from 'ionicons/icons';
import { Line } from 'react-chartjs-2';
import DashboardLayout from '../components/Dashboard/DashboardLayout';
import styles from '../components/Dashboard/Dashboard.module.css';
import { AnalyticsService, LinkWithStats } from '../api/services/analyticsService';
import '../utils/chartSetup';

interface MetricCardProps {
  icon: string;
  title: string;
  value: string | number;
  trend?: number;
  info?: string;
}

const Home: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [metrics, setMetrics] = useState({
    totalViews: 0,
    totalViewsTrend: 0,
    activeLinks: 0,
    recentViews: 0,
    recentViewsTrend: 0,
    bandwidthUsed: '0 MB',
  });
  const [recentLinks, setRecentLinks] = useState<LinkWithStats[]>([]);
  const [popularLinks, setPopularLinks] = useState<LinkWithStats[]>([]);
  const [activeTab, setActiveTab] = useState<'recent' | 'popular'>('recent');
  const [viewsData, setViewsData] = useState<{
    labels: string[];
    datasets: Array<{
      label: string;
      data: number[];
      borderColor: string;
      tension: number;
    }>;
  }>({
    labels: [],
    datasets: [{
      label: 'Views',
      data: [],
      borderColor: 'rgb(75, 192, 192)',
      tension: 0.4,
    }]
  });

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const [metricsData, linksData, analyticsData] = await Promise.all([
          AnalyticsService.getMetrics(),
          AnalyticsService.getRecentLinks(),
          AnalyticsService.getViewsAnalytics()
        ]);

        setMetrics({
          totalViews: metricsData.totalViews,
          totalViewsTrend: metricsData.totalViewsTrend ?? 0,
          activeLinks: metricsData.activeLinks,
          recentViews: Object.entries(analyticsData.hourlyViews)
            .sort(([a], [b]) => b.localeCompare(a))[0]?.[1] ?? 0,
          recentViewsTrend: analyticsData.recentViewsTrend ?? 0,
          bandwidthUsed: formatBytes(metricsData.totalBandwidth)
        });
        setRecentLinks(linksData.recentLinks);
        setPopularLinks(linksData.popularLinks);
        
        // Transform analytics data for the chart
        const sortedDailyViews = Object.entries(analyticsData.hourlyViews)
          .sort(([a], [b]) => a.localeCompare(b));
        
        setViewsData({
          labels: sortedDailyViews.map(([date]) => {
            // Format date as "MMM D" (e.g., "Jan 15")
            const [year, month, day] = date.split('-');
            return new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
              .toLocaleDateString('en-GB', { month: 'short', day: 'numeric' });
          }),
          datasets: [{
            label: 'Daily Views',
            data: sortedDailyViews.map(([_, count]) => count),
            borderColor: 'rgb(75, 192, 192)',
            tension: 0.4
          }]
        });
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const MetricCard: React.FC<MetricCardProps> = ({ icon, title, value, trend, info }) => (
    <IonCard className={styles.card}>
      <IonCardContent className="ion-padding">
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px' }}>
          <IonIcon
            icon={icon}
            style={{
              fontSize: '24px',
              color: 'var(--ion-color-primary)',
              marginRight: '12px'
            }}
          />
          <span style={{ color: 'var(--ion-color-medium)', fontSize: '0.9rem', marginRight: '8px' }}>
            {title}
          </span>
          {info && (
            <IonIcon
              icon={informationCircleOutline}
              style={{
                fontSize: '16px',
                color: 'var(--ion-color-medium)',
                cursor: 'help',
                opacity: 0.7,
                transition: 'opacity 0.2s ease'
              }}
              onMouseEnter={(e) => {
                const target = e.currentTarget;
                target.style.opacity = '1';
                const tooltip = document.createElement('div');
                tooltip.textContent = info;
                tooltip.style.cssText = `
                  position: absolute;
                  background: var(--ion-color-dark);
                  color: white;
                  padding: 8px 12px;
                  border-radius: 4px;
                  font-size: 0.8rem;
                  max-width: 250px;
                  z-index: 1000;
                  box-shadow: 0 2px 8px rgba(0,0,0,0.2);
                `;
                const rect = target.getBoundingClientRect();
                tooltip.style.top = `${rect.bottom + 5}px`;
                tooltip.style.left = `${rect.left}px`;
                tooltip.id = 'metric-tooltip';
                document.body.appendChild(tooltip);
              }}
              onMouseLeave={() => {
                const tooltip = document.getElementById('metric-tooltip');
                if (tooltip) {
                  tooltip.remove();
                }
              }}
            />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline' }}>
          <h2 style={{ 
            margin: '0',
            fontSize: '1.8rem',
            fontWeight: '600',
            color: 'var(--ion-color-dark)'
          }}>
            {isLoading ? <IonSkeletonText animated style={{ width: '80px' }} /> : value}
          </h2>
          {trend !== undefined && (
            <span style={{
              marginLeft: '8px',
              fontSize: '0.9rem',
              color: trend >= 0 ? 'var(--ion-color-success)' : 'var(--ion-color-danger)'
            }}>
              {trend === 1000 ? (
                <span style={{ color: 'var(--ion-color-success)' }}>New</span>
              ) : (
                `${trend > 0 ? '+' : ''}${trend}%`
              )}
            </span>
          )}
        </div>
      </IonCardContent>
    </IonCard>
  );

  const LinkList: React.FC<{ links: LinkWithStats[] }> = ({ links }) => (
    <>
      {links.map((link, index) => (
        <IonItem 
          key={`${link.id}-${activeTab}`}
          button
          routerLink={`/links/${link.id}`}
          lines={index === links.length - 1 ? "none" : "full"}
          className={styles.cardContent}
        >
          <IonIcon icon={timeOutline} slot="start" />
          <IonLabel>
            <h3>{link.name}</h3>
            <p className={styles.linkMeta}>
              <span>Created: {new Date(link.createdAt).toLocaleDateString()}</span>
              <span>Last viewed: {new Date(link.lastAccessed).toLocaleDateString()}</span>
            </p>
          </IonLabel>
          <div slot="end" className={styles.statItem}>
            <IonIcon icon={eyeOutline} />
            {link.viewCount}
          </div>
        </IonItem>
      ))}
    </>
  );

  return (
    <DashboardLayout>
      <IonPage>
        <IonContent className="ion-padding">
          <IonGrid>
            {/* Metrics Row */}
            <IonRow>
              <IonCol size="12" sizeMd="3">
                <MetricCard
                  icon={eyeOutline}
                  title="Total Views (All Time)"
                  value={metrics.totalViews.toLocaleString()}
                  trend={metrics.totalViewsTrend}
                  info="Total number of views for all your links since account creation. Trend compares last 7 days to previous 7 days."
                />
              </IonCol>
              <IonCol size="12" sizeMd="3">
                <MetricCard
                  icon={linkOutline}
                  title="Active Links (Last 30d)"
                  value={metrics.activeLinks}
                  info="Links accessed in the last 30 days."
                />
              </IonCol>
              <IonCol size="12" sizeMd="3">
                <MetricCard
                  icon={trendingUpOutline}
                  title="Views (Last 24h)"
                  value={metrics.recentViews}
                  trend={metrics.recentViewsTrend}
                  info="Total views in the last 24 hours. Trend compares to the previous 24 hours."
                />
              </IonCol>
              <IonCol size="12" sizeMd="3">
                <MetricCard
                  icon={cloudDownloadOutline}
                  title="Bandwidth Used (All Time)"
                  value={metrics.bandwidthUsed}
                  info="Total bandwidth used by your account since creation."
                />
              </IonCol>
            </IonRow>

            {/* Chart and Recent Activity */}
            <IonRow>
              {/* Views Chart */}
              <IonCol size="12" sizeMd="8">
                <IonCard className={styles.card}>
                  <IonCardHeader>
                    <IonCardTitle>Views Over Time (Last 30 Days)</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    {isLoading ? (
                      <IonSkeletonText animated style={{ height: '200px' }} />
                    ) : (
                      <Line data={viewsData} options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false
                          },
                          tooltip: {
                            callbacks: {
                              title: (items) => {
                                return items[0].label;
                              },
                              label: (item) => {
                                return `${item.parsed.y} views`;
                              }
                            }
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            ticks: {
                              precision: 0
                            }
                          },
                          x: {
                            grid: {
                              display: false
                            }
                          }
                        }
                      }} style={{ height: '300px' }} />
                    )}
                  </IonCardContent>
                </IonCard>
              </IonCol>

              {/* Recent Activity */}
              <IonCol size="12" sizeMd="4">
                <IonCard className={styles.card}>
                  <IonCardHeader>
                    <div className={styles.cardHeaderWithTabs}>
                      <IonCardTitle>Link Activity</IonCardTitle>
                      <div className={styles.tabs}>
                        <button 
                          className={`${styles.tab} ${activeTab === 'recent' ? styles.active : ''}`}
                          onClick={() => setActiveTab('recent')}
                        >
                          Recent
                        </button>
                        <button 
                          className={`${styles.tab} ${activeTab === 'popular' ? styles.active : ''}`}
                          onClick={() => setActiveTab('popular')}
                        >
                          Popular
                        </button>
                      </div>
                    </div>
                  </IonCardHeader>
                  <IonCardContent>
                    {isLoading ? (
                      Array(4).fill(0).map((_, i) => (
                        <IonItem key={i} lines={i === 3 ? "none" : "full"}>
                          <IonSkeletonText animated style={{ width: '100%' }} />
                        </IonItem>
                      ))
                    ) : (
                      <LinkList links={activeTab === 'recent' ? recentLinks : popularLinks} />
                    )}
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>

            {/* Quick Actions */}
            <IonRow>
              <IonCol size="12">
                <IonCard className={styles.card}>
                  <IonCardHeader>
                    <IonCardTitle>Quick Actions</IonCardTitle>
                  </IonCardHeader>
                  <IonCardContent>
                    <IonRow>
                      <IonCol size="12" sizeMd="4">
                        <IonButton expand="block" routerLink="/links/new">
                          <IonIcon slot="start" icon={linkOutline} />
                          Create New Link
                        </IonButton>
                      </IonCol>
                      <IonCol size="12" sizeMd="4">
                        <IonButton expand="block" routerLink="/links">
                          <IonIcon slot="start" icon={eyeOutline} />
                          View All Links
                        </IonButton>
                      </IonCol>
                      <IonCol size="12" sizeMd="4">
                        <IonButton expand="block" routerLink="/settings">
                          <IonIcon slot="start" icon={peopleOutline} />
                          Manage Team
                        </IonButton>
                      </IonCol>
                    </IonRow>
                  </IonCardContent>
                </IonCard>
              </IonCol>
            </IonRow>
          </IonGrid>
        </IonContent>
      </IonPage>
    </DashboardLayout>
  );
};

export default Home; 